

export function applyFilters( data, filters, reportConfig ) {
    console.log( 'applyFilters dateRange:', filters.dateRange );
    return data.filter( ( item ) => {
        // Handle date filtering
        if ( filters.dateRange !== 'all' ) {
            if ( !applyFilters_itemPassesDateRange( item, reportConfig, filters ) ) {
                return false;
            }
        }

        // Loan Purpose
        if ( filters.loanPurpose.length > 0 && !filters.loanPurpose.includes( item.purpose ) ) {
            return false;
        }

        // Loan Type
        if ( filters.loanType.length > 0 && !filters.loanType.includes( item.loanType ) ) {
            return false;
        }

        // Loan Source
        if ( filters.loanSource.length > 0 && !filters.loanSource.includes( item.loanSource ) ) {
            return false;
        }

        // Referral Source
        if ( filters.referralSource.length > 0 && !filters.referralSource.includes( item.referralSource ) ) {
            return false;
        }

        // State
        if ( filters.state.length > 0 && !filters.state.includes( item.subjectState ) ) {
            return false;
        }

        // County
        if ( filters.county.length > 0 && !filters.county.includes( item.subjectCounty?.trim().toLowerCase() ) ) {
            return false;
        }

        // City
        if ( filters.city.length > 0 && !filters.city.includes( item.subjectCity?.trim().toLowerCase() ) ) {
            return false;
        }

        // Zip
        if ( filters.zip.length > 0 && !filters.zip.includes( item.subjectZip ) ) {
            return false;
        }

        // Rank
        if ( filters.rank.length > 0 && !filters.rank.includes( item.rank ) ) {
            return false;
        }

        // Partner Rank
        if ( filters.partnerRank.length > 0 && !filters.partnerRank.includes( item.partnerRank ) ) {
            return false;
        }

        // Closed
        if ( filters.closed === true && item.closed !== true ) {
            return false;
        }

        // Referred
        if ( filters.referred === true && item.referred !== true ) {
            return false;
        }

        // Purchase Price min/max
        const pp = normalizeNumber( item.purchasePrice );
        let minSet = filters.minPurchasePrice !== null && filters.minPurchasePrice !== '';
        let maxSet = filters.maxPurchasePrice !== null && filters.maxPurchasePrice !== '';

        if ( minSet && pp === null ) return false;
        if ( maxSet && pp === null && minSet ) return false; // exclude if both set

        if ( minSet && pp < filters.minPurchasePrice ) return false;
        if ( maxSet && pp > filters.maxPurchasePrice ) return false;

        // Loan Amount min/max
        const loanAmount = normalizeNumber( item.amount );
        minSet = filters.minLoanAmount !== null && filters.minLoanAmount !== '';
        maxSet = filters.maxLoanAmount !== null && filters.maxLoanAmount !== '';

        if ( minSet && loanAmount === null ) return false;
        if ( maxSet && loanAmount === null && minSet ) return false; // exclude if both set

        if ( minSet && loanAmount < filters.minLoanAmount ) return false;
        if ( maxSet && loanAmount > filters.maxLoanAmount ) return false;

        // Interest rate (min/max)
        const rate = normalizeRate( item.rate );
        const minRateSet = filters.minInterestRate !== null && filters.minInterestRate !== '';
        const maxRateSet = filters.maxInterestRate !== null && filters.maxInterestRate !== '';

        if ( minRateSet && rate === null ) return false;
        if ( maxRateSet && rate === null && minRateSet ) return false;
        if ( minRateSet && rate < filters.minInterestRate ) return false;
        if ( maxRateSet && rate > filters.maxInterestRate ) return false;
        
        return true;
    } );
}

// ---------- helpers ----------
function startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}

function endOfDay(d) {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
}

function addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
}
  
function startOfYear(date) {
    return new Date(date.getFullYear(), 0, 1);
}

function startOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}

function endOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}

/**
 * startOfWeek / endOfWeek
 * - weekStart: 0 = Sunday, 1 = Monday, etc. (default 0 for US-style week starting Sunday)
 */
function startOfWeek(d, weekStart = 0) {
    // normalize to day start
    const dt = startOfDay(d);
    const day = dt.getDay(); // 0..6 (Sunday..Saturday)
    // number of days to subtract to get to weekStart
    let diff = (day - weekStart);
    if (diff < 0) diff += 7;
    return addDays(dt, -diff);
}

function endOfWeek(d, weekStart = 0) {
    const s = startOfWeek(d, weekStart);
    return endOfDay(addDays(s, 6));
}

// returns day-of-year 1..366 (handles leap years)
function dayOfYear(date) {
    const start = startOfYear(date);
    return Math.floor((date - start) / 86400000) + 1;
}
  
// Normalize dateFields so each entry is { key, ignoreYear: boolean }
function normalizeDateFields(dateFields) {
    return (dateFields || []).map(f => {
        if (typeof f === 'string') return { key: f, ignoreYear: false };
        // allow already-expanded object { key, ignoreYear, ... }
        return { key: f.key, ignoreYear: !!f.ignoreYear };
    });
}
  
// ---------- getDateRange (extended) ----------
function getDateRange(filters) {
    const now = new Date();
    let start = null;
    let end = null;
  
    const dr = (filters && filters.dateRange) || 'all';
  
    // generic parsing for values like next7days, next30days, next365days
    const nextMatch = /^next(\d+)days$/i.exec(dr);
    if (nextMatch) {
        const days = parseInt(nextMatch[1], 10);
        start = new Date(now);
        end = addDays(now, days);
        return [start, end];
    }

    // generic parsing for values like last7days, last30days, last365days
    const lastMatch = /^last(\d+)days$/i.exec(dr);
    if (lastMatch) {
        const days = parseInt(lastMatch[1], 10);
        end = new Date(now);
        start = addDays(now, days * -1);
        return [start, end];
    }
  
    switch (dr) {
        case 'next7days':
        case 'next30days':
        case 'next90days':
        case 'next180days':
        case 'next365days':
        case 'last7days':
        case 'last30days':
        case 'last90days':
        case 'last180days':
        case 'last365days':
            // fall-through to generic above if pattern matches, but keep cases for clarity
            // handled above via regex
            break;
    
        // --- single day options ---
        case 'today':
            start = startOfDay(now);
            end = endOfDay(now);
            break;
  
        case 'tomorrow':
            start = startOfDay(addDays(now, 1));
            end = endOfDay(addDays(now, 1));
            break;

        // --- this week / next week ---
        case 'thisWeek': {
            // default weekStart = 0 (Sunday). Use 1 for Monday if you prefer.
            const weekStart = (filters && filters.weekStart !== undefined) ? filters.weekStart : 0;
            start = startOfWeek(now, weekStart);
            end = endOfWeek(now, weekStart);
            break;
        }
    
        case 'nextWeek': {
            const weekStart = (filters && filters.weekStart !== undefined) ? filters.weekStart : 0;
            // get start of current week then add 7 days
            start = addDays(startOfWeek(now, weekStart), 7);
            end = endOfWeek(start, weekStart);
            break;
        }
    
        // --- this month / next month ---
        case 'thisMonth':
            start = startOfMonth(now);
            end = endOfMonth(now);
            break;
    
        case 'nextMonth': {
            const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
            start = startOfMonth(next);
            end = endOfMonth(next);
            break;
        }

        case 'ytd':
            start = new Date(now.getFullYear(), 0, 1);
            end = new Date(now);
            break;
    
        case 'thisYear':
            start = new Date(now.getFullYear(), 0, 1);
            end = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
            break;
    
        case 'custom':
            start = filters.startDate ? new Date(filters.startDate) : null;
            end = filters.endDate ? new Date(filters.endDate) : null;
            break;
    
        case 'all':
        default:
            start = null;
            end = null;
            break;
    }
  
    return [start, end];
}
  
// ---------- applyFilters date portion ----------
function applyFilters_itemPassesDateRange(item, reportConfig = {}, filters = {}) {
    const dateFieldDefs = normalizeDateFields(reportConfig.dateFields || ['createdDate']);
  
    if (filters.dateRange !== 'all') {
        const [start, end] = getDateRange(filters);
        // attach resolved start/end back to filters like you did before
        filters.startDate = start;
        filters.endDate = end;
    
        // if we don't have a start/end, treat as 'all'
        if (!start && !end) return true;
    
        const hasValidDate = dateFieldDefs.some((fieldDef) => {
            const raw = item[fieldDef.key];
            if (!raw) return false;
    
            // try parse
            const itemDate = new Date( raw.match( 'T' ) ? raw : `${raw}T12:00:00` );
            if (isNaN(itemDate)) return false;
    
            // If we are ignoring the year (birthdays), compare by month/day (day-of-year)
            if (fieldDef.ignoreYear) {
                // We'll map the item's month/day into the YEAR of "start" for consistent comparison.
                // This handles wrapping ranges (e.g., Dec -> Jan).
                const refYear = start ? start.getFullYear() : (new Date()).getFullYear();
        
                // item mapped to refYear, but original item might be Feb 29 on a leap year --
                // if refYear isn't leap and item is Feb 29, map to Feb 28 as a best-effort.
                let mappedItemDate;
                try {
                    mappedItemDate = new Date(refYear, itemDate.getMonth(), itemDate.getDate());
                    if (isNaN(mappedItemDate)) {
                        // fallback for Feb 29 on non-leap refYear
                        mappedItemDate = new Date(refYear, itemDate.getMonth(), Math.min(itemDate.getDate(), 28));
                    }
                } catch (e) {
                    mappedItemDate = new Date(refYear, itemDate.getMonth(), itemDate.getDate());
                }
        
                // compute day-of-year values for comparison
                const itemDOY = dayOfYear(mappedItemDate);
                const startDOY = start ? dayOfYear(new Date(refYear, start.getMonth(), start.getDate())) : null;
                const endDOY = end ? dayOfYear(new Date(refYear, end.getMonth(), end.getDate())) : null;
        
                // If start or end is null, behave permissively
                if (startDOY == null && endDOY == null) return true;
                if (startDOY == null) return itemDOY <= endDOY;
                if (endDOY == null) return itemDOY >= startDOY;
        
                // If range does not wrap year (start <= end)
                if (startDOY <= endDOY) {
                    return itemDOY >= startDOY && itemDOY <= endDOY;
                } else {
                    // Wraps year boundary (e.g., Nov 15 -> Feb 15)
                    return itemDOY >= startDOY || itemDOY <= endDOY;
                }
            } else {
                // Normal full-date comparison (inclusive)
                if (start && itemDate < start) return false;
                if (end && itemDate > end) return false;
                return true;
            }
        });
    
        if (!hasValidDate) return false;
    }
  
    // if filters.dateRange === 'all', pass through
    return true;
}  

function normalizeNumber( value ) {
    if ( value === null || value === undefined ) return null;
  
    if ( typeof value === 'number' ) return value;
  
    if ( typeof value === 'string' ) {
        const cleaned = value.replace( /[$,]/g, '' ).trim();
        const num = parseFloat( cleaned );
        return isNaN( num ) ? null : num;
    }
  
    return null;
}

function normalizeRate( value ) {
    if ( value === null || value === undefined ) return null;
  
    let num;
  
    if ( typeof value === 'number' ) {
        num = value;
    } else if ( typeof value === 'string' ) {
        const cleaned = value.replace( /[%]/g, '' ).trim();
        num = parseFloat( cleaned );
    } else {
        return null;
    }
  
    if ( isNaN( num ) ) return null;
  
    // If it's ≤ 1, assume it's a decimal fraction (e.g., 0.0625) → convert to percent
    if ( num <= 1 ) num = num * 100;
  
    return num;
}
  